import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons, PayPalCardFieldsProvider, PayPalNumberField, PayPalExpiryField, PayPalCVVField, PayPalNameField, usePayPalCardFields } from "@paypal/react-paypal-js";
import { CreditCard, Wallet, Loader2, Lock } from "lucide-react";
import api from "@/lib/api";

let cfgPromise;
const getCfg = () => (cfgPromise ||= api.get("/paypal/config").then((r) => r.data));
const errMsg = (e) => e?.response?.data?.detail || "Pagamento non riuscito";
const cardStyle = { input: { "font-size": "15px", "font-family": "system-ui, sans-serif", padding: "12px" }, ".invalid": { color: "#dc2626" } };

function CardSubmit({ label, busy, setBusy }) {
  const { cardFieldsForm } = usePayPalCardFields();
  const go = async () => {
    if (!cardFieldsForm) return;
    const st = await cardFieldsForm.getState();
    if (!st.isFormValid) { toast.error("Controlla i dati della carta"); return; }
    setBusy(true);
    try { await cardFieldsForm.submit(); } catch { setBusy(false); toast.error("Carta non accettata"); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} className="w-full bg-[#E3FF00] text-[#09090b] font-semibold rounded-xl py-3 inline-flex items-center justify-center gap-2 hover:bg-[#cbff00] disabled:opacity-60" data-testid="paypal-card-submit">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {label}
    </button>
  );
}

// mode "vault": salva carta/PayPal senza addebito (onSaved). mode "order": pagamento singolo (createOrder(source) -> id, onPaid(orderID)).
export function PayPalCheckout({ mode, createOrder, onPaid, onSaved, label }) {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("card");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getCfg().then(setCfg).catch(() => setCfg({ configured: false })); }, []);
  if (!cfg) return <p className="text-sm text-slate-500">Caricamento pagamenti…</p>;
  if (!cfg.configured) return <p className="text-sm text-rose-600" data-testid="paypal-not-configured">Pagamenti non disponibili al momento.</p>;
  const finish = async (fn) => { try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const handlers = (source) => (mode === "vault"
    ? { createVaultSetupToken: () => api.post("/paypal/vault/setup-token", { method: source }).then((r) => r.data.id).catch((e) => { toast.error(errMsg(e)); throw e; }),
        onApprove: (d) => finish(async () => { const { data } = await api.post("/paypal/vault/confirm", { setup_token: d.vaultSetupToken }); onSaved?.(data.method); }) }
    : { createOrder: () => createOrder(source).catch((e) => { toast.error(errMsg(e)); throw e; }),
        onApprove: (d) => finish(() => onPaid(d.orderID)) });
  const tabBtn = (k, Icon, l) => (
    <button type="button" onClick={() => setTab(k)} className={`rounded-xl border-2 p-3 text-sm font-semibold inline-flex items-center justify-center gap-2 ${tab === k ? "border-slate-900 bg-[#E3FF00]" : "border-slate-200 bg-white"}`} data-testid={`paypal-tab-${k}`}><Icon className="h-4 w-4" /> {l}</button>
  );
  return (
    <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "EUR", intent: "capture", components: "buttons,card-fields", locale: "it_IT" }}>
      <div className="space-y-3" data-testid="paypal-checkout">
        <div className="grid grid-cols-2 gap-2">{tabBtn("card", CreditCard, "Carta")}{tabBtn("paypal", Wallet, "PayPal")}</div>
        {tab === "card" ? (
          <PayPalCardFieldsProvider key={`card-${mode}`} {...handlers("card")} onError={() => { setBusy(false); toast.error("Errore carta"); }} style={cardStyle}>
            <div className="space-y-1" data-testid="paypal-card-fields">
              <PayPalNameField placeholder="Intestatario carta" />
              <PayPalNumberField placeholder="Numero carta" />
              <div className="grid grid-cols-2 gap-2"><PayPalExpiryField placeholder="MM/AA" /><PayPalCVVField placeholder="CVV" /></div>
            </div>
            <CardSubmit label={label} busy={busy} setBusy={setBusy} />
          </PayPalCardFieldsProvider>
        ) : (
          <div data-testid="paypal-buttons"><PayPalButtons key={`pp-${mode}`} fundingSource="paypal" style={{ layout: "vertical", shape: "rect", label: "paypal", height: 45 }} {...handlers("paypal")} onError={() => toast.error("Errore PayPal")} /></div>
        )}
        <p className="text-[11px] text-slate-500 flex items-center gap-1"><Lock className="h-3 w-3" /> I dati della carta vanno direttamente a PayPal: Mypasspartu non li vede né li conserva.{cfg.mode === "sandbox" ? " · Modalità di prova (Sandbox)" : ""}</p>
      </div>
    </PayPalScriptProvider>
  );
}

export const pmLabel = (pm) => (pm?.type === "card" ? `${pm.brand || "Carta"} •••• ${pm.last4}` : `PayPal ${pm?.email || ""}`);

export function SavedMethod({ method, onChange, startEdit = false }) {
  const [edit, setEdit] = useState(!method || startEdit);
  if (method && !edit) return (
    <div className="rounded-xl border-2 border-slate-900 bg-[#E3FF00]/30 p-3 flex items-center gap-3" data-testid="saved-method">
      {method.type === "card" ? <CreditCard className="h-5 w-5" /> : <Wallet className="h-5 w-5" />}
      <span className="flex-1 text-sm font-semibold" data-testid="saved-method-label">{pmLabel(method)}</span>
      <button type="button" onClick={() => setEdit(true)} className="bg-[#E3FF00] text-[#09090b] text-xs font-semibold px-3 py-1.5 rounded-lg" data-testid="saved-method-change">Cambia</button>
    </div>
  );
  return <PayPalCheckout mode="vault" label="Salva metodo di pagamento" onSaved={(pm) => { setEdit(false); onChange(pm); toast.success("Metodo di pagamento salvato"); }} />;
}
